import { useContext, useState } from "react";
import styled from "styled-components";
import tw from "twin.macro";
import Card from "@/atoms/Card";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import * as Yup from "yup";
import { Field, Form, Formik } from "formik";
import Select from "@/atoms/Select";
import { MONTHS_OF_THE_YEAR } from "constants/dates";
import useFitText from "use-fit-text";
import cx from "classnames";
import { getSelectionFromValue, submitFormik } from "utils/forms";
import ContentfulFormikField from "utils/contentful/ContentfulFormikField";
import PropTypes from "prop-types";
import { getBirthYears } from "utils/date";
import { Image } from "@literati/public-ui-shared-react";
import DLSHeader from "components/DLS/DLSHeader";
import DLSStickyFooter from "components/DLS/DLSStickyFooter";
import { CheckoutContext } from "contexts/CheckoutProvider";
import DupSubWarningModal from "components/GuidedSignUp/DupSubWarningModal";

const StyledPersonalizationForm = styled.section`
  .personalization-page-video {
    margin-bottom: -30%;
  }
  .personalize-image img {
    width: 100% !important;
  }
  .pre-form-text {
    ${tw`py-lg text-center mx-auto`};

    ${({ theme }) => theme.media["lg"]} {
      ${tw`text-left ml-0`};
    }
  }

  form {
    ${tw`p-lg`};
    ${({ theme }) => theme.media["md"]} {
      ${tw`flex flex-col justify-center px-12`};
    }
    ${({ theme }) => theme.media["lg"]} {
      ${tw`px-24`};
    }
  }

  .birthday {
    ${tw`grid`};
    grid-template-columns: 4fr 2fr;
    grid-column-gap: 1rem;
  }

  .img-wrapper {
    border-radius: 12px 12px 0 0;
    overflow: hidden;
    position: relative;
    font-size: 1.5rem;
    ${({ theme }) => theme.media["md"]} {
      border-radius: 0 12px 12px 0;
    }
  }

  .child-name-container {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate3d(-50%, -50%, 0);
    width: 35%;
    height: 40px;
    text-align: center;
  }

  .card {
    ${tw`grid mx-auto`};
    max-width: 400px;
    ${({ theme }) => theme.media["md"]} {
      max-width: 100%;
      grid-template-columns: 1fr 1fr;

      > div {
        grid-row: 1;
        grid-column: 2;
      }

      > form {
        grid-row: 1;
        grid-column: 1;
      }
    }
    ${({ theme }) => theme.media["lg"]} {
      ${tw`mt-6`};
    }
  }
`;

const PreFormText = ({ heading, subhead, className }) => (
  <div className={cx("pre-form-text", className)}>
    <h1 className="heading mb-3">{heading}</h1>
    {documentToReactComponents(subhead)}
  </div>
);

const NameContainer = ({ name }) => {
  const { fontSize, ref } = useFitText();
  return (
    <div style={{ fontSize }} className="child-name-container" ref={ref}>
      {name}
    </div>
  );
};

/**
 * Form to input child name and age (birth month/year).
 *
 * @param {Object} props
 * @param {Object} props.entry
 * @param {Function} props.updateChildInfo
 * @param {Boolean} props.useAge
 * @param {Object} props.initialValues
 * @param {Function} props.routeNext
 */
const CheckoutPersonalizationForm = ({
  entry,
  updateChildInfo,
  useAge,
  initialValues,
  routeNext,
  flow,
}) => {
  const [duplicateSubWarningModalOpen, setDuplicateSubWarningModalOpen] =
    useState(false);
  const [childName, setChildName] = useState("");
  const [subId, setSubId] = useState(null);
  const { updateSelectedClub } = useContext(CheckoutContext);
  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required("Required.")
      .max(30, "Maximum of 30 characters allowed."),
    birthMonth: Yup.number().when("$useAge", (_, schema) =>
      useAge ? schema : schema.required("Required.")
    ),
    birthYear: Yup.number().when("$useAge", (_, schema) =>
      useAge ? schema : schema.required("Required.")
    ),
    age: Yup.number().when("$useAge", (_, schema) =>
      useAge
        ? schema
            .min(0, "Must be greater or equal to 0")
            .max(100, "Must be less than 100")
            .integer("Please round to the nearest whole number")
            .required("Required.")
        : schema
    ),
  });

  const onSuccess = () => {
    window.dataLayer?.push({
      event: "personalize submit",
      eventCategory: "KidsSubscription",
    });
    routeNext?.();
  };

  const { heading, subhead, references } = entry;
  const getImageEntry = (device) =>
    references.find((r) => r.type === "image" && r.device === device);
  const { image: mobileImage } = getImageEntry("mobile");
  const { image: desktopImage } = getImageEntry("desktop");

  const handleSubmit = async (values) => {
    const childInfo = await updateChildInfo(values);
    const suggestedClub =
      childInfo?.primarySuggestedClub ||
      childInfo?.secondarySuggestedClubs?.[0];

    if (flow === "subscribe") {
      await updateSelectedClub(suggestedClub);
      return childInfo;
    } else {
      return childInfo;
    }
  };

  const onDupSubModalConfirm = async (values) => {
    setDuplicateSubWarningModalOpen(false);
    await handleSubmit(values);
    onSuccess();
  };

  const onSubmit = async (values, formikProps) => {
    await submitFormik({
      onSubmit: () => handleSubmit(values),
      onSuccess: async (childInfo) => {
        if (childInfo?.possibleDuplicateSubId) {
          setSubId(childInfo?.possibleDuplicateSubId);
          setChildName(values.firstName);
          setDuplicateSubWarningModalOpen(true);
        } else {
          return onSuccess();
        }
      },
      formikProps,
    });
  };

  const updateMobileScroll = () => {
    if (typeof window !== "undefined" && typeof navigator !== "undefined") {
      if (/iPhone/i.test(navigator.userAgent)) {
        window.scrollTo(0, 270);
      }
    }
  };

  const ContentfulField = (props) => (
    <ContentfulFormikField {...props} entry={entry} />
  );

  return (
    <>
      <DLSHeader />
      <StyledPersonalizationForm id="personalization-form">
        <div className="content-spacing">
          <PreFormText
            heading={heading}
            subhead={subhead}
            className={"lg:hidden"}
          />
          <Card>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              enableReinitialize={true}
              context={{ useAge }}
            >
              {({
                setValues,
                setTouched,
                values,
                isSubmitting,
                handleSubmit,
                touched,
                getFieldMeta,
              }) => {
                const getError = (field) => {
                  const meta = getFieldMeta(field);
                  return meta?.touched && meta?.error;
                };
                const handleSelect = (field) => (value) => {
                  if (value && value.value) {
                    value = value.value;
                  }
                  setTouched({ ...touched, [field]: true });
                  setValues({ ...values, [field]: value });
                };

                return (
                  <>
                    <div className="img-wrapper personalize-image">
                      <div className={"md:hidden"}>
                        <Image
                          sizes="(min-width: 480px) calc(100vw - 6rem), calc(100vw - 2.5rem)"
                          src={mobileImage.file.url}
                          {...mobileImage.file.details.image}
                          layout="responsive"
                          alt=""
                        />
                      </div>
                      <div className={"hidden md:block"}>
                        <Image
                          sizes="(min-width: 480px) calc(100vw - 6rem), (min-width: 720px) calc((100vw - 1140px - 6rem) / 2)"
                          src={desktopImage.file.url}
                          {...desktopImage.file.details.image}
                          layout="responsive"
                          alt=""
                        />
                      </div>
                      <NameContainer name={values.firstName} />
                    </div>
                    <Form id="personalization-form" onSubmit={handleSubmit}>
                      <PreFormText
                        heading={heading}
                        subhead={subhead}
                        className={"hidden lg:block mb-4"}
                      />
                      <ContentfulField
                        name="firstName"
                        onFocus={updateMobileScroll}
                      />

                      {useAge ? (
                        <ContentfulField
                          type={"number"}
                          name="age"
                          min={0}
                          max={100}
                        />
                      ) : (
                        <>
                          <div className="text-sm mb-1">
                            {"When's their birthday?"}
                          </div>
                          <div className="birthday">
                            <Field
                              as={Select}
                              id={"birthMonth"}
                              name="birthMonth"
                              placeholder="Month"
                              items={MONTHS_OF_THE_YEAR}
                              itemToString={(item) => item.name}
                              onChange={handleSelect("birthMonth")}
                              btnWidth="100%"
                              selectedItem={
                                values.birthMonth
                                  ? getSelectionFromValue(
                                      MONTHS_OF_THE_YEAR,
                                      +values.birthMonth
                                    )
                                  : null
                              }
                              error={getError("birthMonth")}
                            />
                            <Field
                              as={Select}
                              name="birthYear"
                              id="birthYear"
                              placeholder="Year"
                              items={getBirthYears()}
                              itemToString={(item) => item.toString()}
                              onChange={handleSelect("birthYear")}
                              btnWidth="100%"
                              selectedItem={values?.birthYear?.toString() || ""}
                              error={getError("birthYear")}
                            />
                          </div>
                        </>
                      )}
                      <DLSStickyFooter
                        ctaButtonProps={{
                          type: "submit",
                          loading: isSubmitting,
                        }}
                        suppressBackButton
                      />
                    </Form>
                    <DupSubWarningModal
                      isOpen={duplicateSubWarningModalOpen}
                      onClose={() => setDuplicateSubWarningModalOpen(false)}
                      childName={childName}
                      onDupSubModalConfirm={onDupSubModalConfirm}
                      subId={subId}
                      formValues={values}
                    />
                  </>
                );
              }}
            </Formik>
          </Card>
        </div>
      </StyledPersonalizationForm>
    </>
  );
};

CheckoutPersonalizationForm.propTypes = {
  entry: PropTypes.object.isRequired,
  updateChildInfo: PropTypes.func.isRequired,
  useAge: PropTypes.bool,
  initialValues: PropTypes.shape({
    firstName: PropTypes.string,
    birthYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    birthMonth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  routeNext: PropTypes.func.isRequired,
};

export default CheckoutPersonalizationForm;
